import { Group } from '@app/shared/models/group';
import { Role } from '@app/shared/models/role';
import { User } from '@app/shared/models/user';
import { Action, createReducer, on } from '@ngrx/store';
import { GroupsActions } from '../actions';


export const groupsFeatureKey = 'groups';

export interface State {
  groups: Group[];
  groupsLoading: boolean;
  groupsLoaded: boolean;
  groupsError: any;

  groupDetails: Group;
  groupDetailsLoading: boolean;
  groupDetailsLoaded: boolean;
  groupDetailsError: any;

  groupRoles: Role[];
  groupRolesLoading: boolean;
  groupRolesLoaded: boolean;
  groupRolesError: any;

  groupMembers: User[];
  groupMembersLoading: boolean;
  groupMembersLoaded: boolean;
  groupMembersError: any;

  createGroupPending: boolean;
  createGroupSuccess: boolean;
  createGroupError: any;

  attachRoleToGroupPending: boolean;
  attachRoleToGroupSuccess: boolean;
  attachRoleToGroupError: any;

  detachRoleFromGroupPending: boolean;
  detachRoleFromGroupSuccess: boolean;
  detachRoleFromGroupError: any;

  attachMemberToGroupPending: boolean;
  attachMemberToGroupSuccess: boolean;
  attachMemberToGroupError: any;

  detachMemberFromGroupPending: boolean;
  detachMemberFromGroupSuccess: boolean;
  detachMemberFromGroupError: any;

  groupUpdating: boolean;
  groupUpdated: boolean;
  groupUpdatingError: any;

  deleteGroupPending: boolean;
  deleteGroupSuccess: boolean;
  deleteGroupError: any;
}

export const initialState: State = {
  groups: null,
  groupsLoading: false,
  groupsLoaded: false,
  groupsError: null,

  groupDetails: null,
  groupDetailsLoading: false,
  groupDetailsLoaded: false,
  groupDetailsError: null,

  groupRoles: null,
  groupRolesLoading: false,
  groupRolesLoaded: false,
  groupRolesError: null,

  groupMembers: null,
  groupMembersLoading: false,
  groupMembersLoaded: false,
  groupMembersError: null,

  createGroupPending: false,
  createGroupSuccess: false,
  createGroupError: null,

  attachRoleToGroupPending: false,
  attachRoleToGroupSuccess: false,
  attachRoleToGroupError: null,

  detachRoleFromGroupPending: false,
  detachRoleFromGroupSuccess: false,
  detachRoleFromGroupError: null,

  attachMemberToGroupPending: false,
  attachMemberToGroupSuccess: false,
  attachMemberToGroupError: null,

  detachMemberFromGroupPending: false,
  detachMemberFromGroupSuccess: false,
  detachMemberFromGroupError: null,

  groupUpdating: false,
  groupUpdated: false,
  groupUpdatingError: null,

  deleteGroupPending: false,
  deleteGroupSuccess: false,
  deleteGroupError: null,
};

const groupsReducer = createReducer(
  initialState,
  // Load groups
  on(GroupsActions.LoadGroups, (state) => ({
    ...state,
    groups: initialState.groups,
    groupsLoading: true,
    groupsLoaded: initialState.groupsLoaded,
    groupsError: initialState.groupsError
  })),
  on(GroupsActions.LoadGroupsSuccess, (state, { groups }) => ({
    ...state,
    groups,
    groupsLoading: false,
    groupsLoaded: true,
    groupsError: null
  })),
  on(GroupsActions.LoadGroupsFailure, (state, { error }) => ({
    ...state,
    groupsLoading: false,
    groupsError: error
  })),
  // Load group details
  on(GroupsActions.LoadGroupDetails, (state) => ({
    ...state,
    groupDetails: initialState.groupDetails,
    groupDetailsLoading: false,
    groupDetailsLoaded: initialState.groupDetailsLoaded,
    groupDetailsError: initialState.groupDetailsError
  })),
  on(GroupsActions.LoadGroupDetailsSuccess, (state, { group }) => ({
    ...state,
    groupDetails: group,
    groupDetailsLoading: false,
    groupDetailsLoaded: true,
    groupDetailsError: null
  })),
  on(GroupsActions.LoadGroupDetailsFailure, (state, { error }) => ({
    ...state,
    groupDetailsLoading: false,
    groupDetailsError: error
  })),
  // Load group roles
  on(GroupsActions.LoadGroupRoles, (state) => ({
    ...state,
    groupRoles: initialState.groupRoles,
    groupRolesLoading: true,
    groupRolesLoaded: initialState.groupRolesLoaded,
    groupRolesError: initialState.groupRolesError
  })),
  on(GroupsActions.LoadGroupRolesSuccess, (state, { roles }) => ({
    ...state,
    groupRoles: roles,
    groupRolesLoading: false,
    groupRolesLoaded: true,
    groupRolesError: null
  })),
  on(GroupsActions.LoadGroupRolesFailure, (state, { error }) => ({
    ...state,
    groupRolesLoading: false,
    groupRolesError: error
  })),
  // Load group members
  on(GroupsActions.LoadGroupMembers, (state) => ({
    ...state,
    groupMembers: initialState.groupMembers,
    groupMembersLoading: true,
    groupMembersLoaded: initialState.groupMembersLoaded,
    groupMembersError: initialState.groupMembersError
  })),
  on(GroupsActions.LoadGroupMembersSuccess, (state, { members }) => ({
    ...state,
    groupMembers: members,
    groupMembersLoading: false,
    groupMembersLoaded: true,
    groupMembersError: null
  })),
  on(GroupsActions.LoadGroupMembersFailure, (state, { error }) => ({
    ...state,
    groupMembersLoading: false,
    groupMembersError: error
  })),
  // Create group
  on(GroupsActions.CreateGroup, (state) => ({
    ...state,
    createGroupPending: true,
    createGroupSuccess: initialState.createGroupSuccess,
    createGroupError: initialState.createGroupError
  })),
  on(GroupsActions.CreateGroupSuccess, (state) => ({
    ...state,
    createGroupPending: false,
    createGroupSuccess: true,
    createGroupError: null
  })),
  on(GroupsActions.CreateGroupFailure, (state, { error }) => ({
    ...state,
    createGroupPending: false,
    createGroupError: error
  })),
  // Attach role to group
  on(GroupsActions.AttachRoleToGroup, (state) => ({
    ...state,
    attachRoleToGroupPending: true,
    attachRoleToGroupSuccess: initialState.attachRoleToGroupSuccess,
    attachRoleToGroupError: initialState.attachRoleToGroupError
  })),
  on(GroupsActions.AttachRoleToGroupSuccess, (state) => ({
    ...state,
    attachRoleToGroupPending: false,
    attachRoleToGroupSuccess: true,
    attachRoleToGroupError: null
  })),
  on(GroupsActions.AttachRoleToGroupFailure, (state, { error }) => ({
    ...state,
    attachRoleToGroupPending: false,
    attachRoleToGroupError: error
  })),
  // Detach role from group
  on(GroupsActions.DetachRoleFromGroup, (state) => ({
    ...state,
    detachRoleFromGroupPending: true,
    detachRoleFromGroupSuccess: initialState.detachRoleFromGroupSuccess,
    detachRoleFromGroupError: initialState.detachRoleFromGroupError
  })),
  on(GroupsActions.DetachRoleFromGroupSuccess, (state) => ({
    ...state,
    detachRoleFromGroupPending: false,
    detachRoleFromGroupSuccess: true,
    detachRoleFromGroupError: null
  })),
  on(GroupsActions.DetachRoleFromGroupFailure, (state, { error }) => ({
    ...state,
    detachRoleFromGroupPending: false,
    detachRoleFromGroupError: error
  })),
  // Attach member to group
  on(GroupsActions.AttachMemberToGroup, (state) => ({
    ...state,
    attachMemberToGroupPending: true,
    attachMemberToGroupSuccess: initialState.attachMemberToGroupSuccess,
    attachMemberToGroupError: initialState.attachMemberToGroupError
  })),
  on(GroupsActions.AttachMemberToGroupSuccess, (state) => ({
    ...state,
    attachMemberToGroupPending: false,
    attachMemberToGroupSuccess: true,
    attachMemberToGroupError: null
  })),
  on(GroupsActions.AttachMemberToGroupFailure, (state, { error }) => ({
    ...state,
    attachMemberToGroupPending: false,
    attachMemberToGroupError: error
  })),
  // Detach member from group
  on(GroupsActions.DetachMemberFromGroup, (state) => ({
    ...state,
    detachMemberFromGroupPending: true,
    detachMemberFromGroupSuccess: initialState.detachMemberFromGroupSuccess,
    detachMemberFromGroupError: initialState.detachMemberFromGroupError
  })),
  on(GroupsActions.DetachMemberFromGroupSuccess, (state) => ({
    ...state,
    detachMemberFromGroupPending: false,
    detachMemberFromGroupSuccess: true,
    detachMemberFromGroupError: null
  })),
  on(GroupsActions.DetachMemberFromGroupFailure, (state, { error }) => ({
    ...state,
    detachMemberFromGroupPending: false,
    detachMemberFromGroupError: error
  })),
  // Update Team
  on(GroupsActions.UpdateGroup, (state) => ({
    ...state,
    groupUpdating: true,
    groupUpdated: initialState.groupUpdated,
    groupUpdatingError: initialState.groupUpdatingError,
  })),
  on(GroupsActions.UpdateGroupSuccess, (state, { group }) => ({
    ...state,
    groupUpdating: initialState.groupUpdating,
    groupUpdated: true,
  })),
  on(GroupsActions.UpdateGroupFailure, (state, { error }) => ({
    ...state,
    groupUpdating: initialState.groupUpdating,
    groupUpdatingError: error
  })),
  // Delete Group
  on(GroupsActions.DeleteGroup, (state) => ({
    ...state,
    deleteGroupPending: true,
    deleteGroupSuccess: initialState.deleteGroupSuccess,
    deleteGroupError: initialState.deleteGroupError
  })),
  on(GroupsActions.DeleteGroupSuccess, (state) => ({
    ...state,
    deleteGroupPending: false,
    deleteGroupSuccess: true,
    deleteGroupError: null
  })),
  on(GroupsActions.DeleteGroupFailure, (state, { error }) => ({
    ...state,
    deleteGroupPending: false,
    deleteGroupError: error
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return groupsReducer(state, action);
}
